/**
 * One scope vocabulary, shared by channel API keys and delegated OAuth tokens.
 *
 * Two credential types with two vocabularies is how a scope ends up meaning
 * something subtly different depending on who holds it.
 */

export const SCOPES = [
  'channel:read',   'channel:write',
  'rewards:read',   'rewards:write',
  'assets:read',    'assets:write',
  'instances:read', 'instances:write',
  'inventory:read',  'inventory:write',
  'wallets:read',   'wallets:write',
  'trades:read',    'trades:write',
  'members:read',
  'schedule:read',
  'webhooks:manage',
  'realtime:subscribe',
  // A Twitch moderator's tools on one channel (ADR 0050 §2). Granted ONLY by
  // an active moderator standing on a channel whose owner switched mod tools
  // on — never by a role, and in no consent label, so nothing can delegate
  // them. Deliberately not `moderation:*`: that is a Twitch scope family, and
  // a grep that cannot tell the two apart has the casing rule's problem.
  'modtools:read', 'modtools:write',
] as const;

/** What an active moderator standing adds to that one channel's grant. */
export const MODTOOLS_SCOPES = ['modtools:read', 'modtools:write'] as const satisfies readonly Scope[];

export type Scope = (typeof SCOPES)[number];

/**
 * Scopes a delegated viewer token may hold.
 *
 * Every one of these is IMPLICITLY SELF-SCOPED: a viewer token carrying
 * `inventory:read` can read only its own inventory, never another member's.
 * That is enforced once, in the principal layer, not per handler — and it has
 * its own multi-tenancy test, because "enforced per handler" means "forgotten
 * in one handler".
 */
export const VIEWER_SCOPES = [
  'channel:read',
  'rewards:read',
  'assets:read',
  'inventory:read',
  // Opening a pack you earned acts on your OWN item and is implicitly
  // self-scoped like every other viewer scope. Deliberately not
  // `instances:write`, which is the channel's authority over everyone's.
  'inventory:write',
  'wallets:read',
  'trades:read',
  'trades:write',
  'schedule:read',
  'realtime:subscribe',
] as const satisfies readonly Scope[];

export type ViewerScope = (typeof VIEWER_SCOPES)[number];

/** Plain-language labels for the OAuth consent screen. Viewers read these. */
export const SCOPE_CONSENT_LABEL: Record<ViewerScope, string> = {
  'channel:read': 'See this channel’s public details',
  'rewards:read': 'See what rewards this channel offers',
  'assets:read': 'See this channel’s artwork',
  'inventory:read': 'See the items you own from this channel',
  'inventory:write': 'Open packs you have earned',
  'wallets:read': 'See your balance on this channel',
  'trades:read': 'See your trades',
  'trades:write': 'Propose, accept and cancel trades on your behalf',
  'schedule:read': 'See when this channel streams',
  'realtime:subscribe': 'Receive live updates',
};

/**
 * A member's role on ONE channel, and what it grants.
 *
 * Roles exist so that a session does not have to carry an arbitrary scope
 * list. The browser never chooses its own scopes: the server reads the role
 * off `channel_members` and looks it up here. That is the whole point — a
 * client that can name its scopes can name more of them.
 *
 * `member` is what everyone gets for a channel they have simply interacted
 * with, and it is exactly VIEWER_SCOPES: seeing a channel's rewards and your
 * own balance is not an administrative act.
 */
export const CHANNEL_ROLES = ['owner', 'editor', 'member'] as const;
export type ChannelRole = (typeof CHANNEL_ROLES)[number];

/**
 * Editors author catalog content. They deliberately do NOT get
 * `channel:write` or `webhooks:manage`: renaming the channel, changing its
 * domains and pointing its webhooks somewhere else are owner decisions, and
 * the last one is an exfiltration path rather than a content one.
 */
const EDITOR_SCOPES = [
  'channel:read',
  'rewards:read', 'rewards:write',
  'assets:read', 'assets:write',
  'instances:read', 'instances:write',
  // A streamer collects from their own channel too.
  'inventory:read', 'inventory:write',
  'members:read',
  'schedule:read',
  'wallets:read',
  'realtime:subscribe',
] as const satisfies readonly Scope[];

export const ROLE_SCOPES: Record<ChannelRole, readonly Scope[]> = {
  owner: [...EDITOR_SCOPES, 'channel:write', 'wallets:write', 'webhooks:manage'],
  editor: EDITOR_SCOPES,
  member: VIEWER_SCOPES,
};

export function scopesForRole(role: string): readonly Scope[] {
  // An unrecognised role grants nothing. A stored role we do not understand is
  // more likely a typo or a rollback than a privilege we meant to hand out.
  return ROLE_SCOPES[role as ChannelRole] ?? [];
}
